import React from "react";
import { CollapsibleFormSection } from "../../common/CollapsibleFormSection";
import { FormTextarea } from "../../common/FormTextarea";
import { SmartGoalSelector } from "../../common/SmartGoalSelector";
import { PersonalisedLearningSupportPlanData } from "src/models/form-types";

interface FormValidation {
  isPurposeValid: boolean;
  isLearningValid: boolean;
  isAdditionalInfoValid: boolean;
  isAdjustmentLevelValid: boolean;
  isStudentInfoValid: boolean;
}

interface LiteracyGoalSectionProps {
  formData: PersonalisedLearningSupportPlanData;
  onTextChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onCheckboxChange: (
    name: string,
    checked: boolean,
    category: keyof PersonalisedLearningSupportPlanData
  ) => void;
  goals: any[];
  isLoading: boolean;
  error: string | null;
  onGenerateGoals: () => Promise<void>;
  onGoalSelect: (goal: string) => void;
  selectedGoal: string | null;
  isFormValid: boolean;
  formValidation: FormValidation;
  className?: string;
}

export const LiteracyGoalSection: React.FC<LiteracyGoalSectionProps> = ({
  formData,
  onTextChange,
  onCheckboxChange,
  goals,
  isLoading,
  error,
  onGenerateGoals,
  onGoalSelect,
  selectedGoal,
  isFormValid,
  formValidation,
  className,
}) => {
  // Handle literacy target radio button changes
  const handleLiteracyTargetChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const event = {
      target: {
        name: "literacyTarget",
        value: e.target.value,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onTextChange(event);
  };

  const handleLiteracyTargetClick = (value: string) => {
    const event = {
      target: {
        name: "literacyTarget",
        value: value,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onTextChange(event);
  };

  return (
    <CollapsibleFormSection
      title="SMART Goal 1: Literacy"
      className={className}
    >
      {/* Literacy Target Radio Buttons */}
      <div className="form-group">
        <p className="form-description">Where would you like to focus?</p>
        <div className="literacy-target-group">
          <div
            className={`literacy-target-option checkbox-item ${
              formData.literacyTarget === "WRITING" ? "selected" : ""
            }`}
            onClick={() => handleLiteracyTargetClick("WRITING")}
          >
            <input
              type="radio"
              id="literacy-writing"
              name="literacyTarget"
              value="WRITING"
              checked={formData.literacyTarget === "WRITING"}
              onChange={handleLiteracyTargetChange}
            />
            <div className="checkbox-custom"></div>
            <label className="checkbox-label" htmlFor="literacy-writing">
              Writing
            </label>
          </div>
          <div
            className={`literacy-target-option checkbox-item ${
              formData.literacyTarget === "SPEAKING_LISTENING" ? "selected" : ""
            }`}
            onClick={() => handleLiteracyTargetClick("SPEAKING_LISTENING")}
          >
            <input
              type="radio"
              id="literacy-speaking"
              name="literacyTarget"
              value="SPEAKING_LISTENING"
              checked={formData.literacyTarget === "SPEAKING_LISTENING"}
              onChange={handleLiteracyTargetChange}
            />
            <div className="checkbox-custom"></div>
            <label className="checkbox-label" htmlFor="literacy-speaking">
              Speaking and listening
            </label>
          </div>
          <div
            className={`literacy-target-option checkbox-item ${
              formData.literacyTarget === "READING_VIEWING" ? "selected" : ""
            }`}
            onClick={() => handleLiteracyTargetClick("READING_VIEWING")}
          >
            <input
              type="radio"
              id="literacy-reading"
              name="literacyTarget"
              value="READING_VIEWING"
              checked={formData.literacyTarget === "READING_VIEWING"}
              onChange={handleLiteracyTargetChange}
            />
            <div className="checkbox-custom"></div>
            <label className="checkbox-label" htmlFor="literacy-reading">
              Reading and Viewing
            </label>
          </div>
        </div>
      </div>

      <FormTextarea
        id="literacyGoalInfo"
        name="literacyGoalInfo"
        label="Give me some information about the student's current literacy level. This will help generate appropriate SMART goals."
        value={formData.literacyGoalInfo}
        onChange={onTextChange}
        rows={4}
      />

      <SmartGoalSelector
        goals={goals.map((goal, index) => ({ id: index + 1, text: goal.text }))}
        isLoading={isLoading}
        error={error}
        onGenerateGoals={onGenerateGoals}
        onGoalSelect={onGoalSelect}
        isFormValid={isFormValid && formData.literacyGoalInfo.trim() !== ""}
        formValidation={formValidation}
        invalidHintTitle="Please complete these required fields:"
        radioGroupName="literacyGoals"
      />
    </CollapsibleFormSection>
  );
};
